'use client';

/**
 * @fileoverview Render-less Discover telemetry mount (WEB-1229).
 *
 * Fires the three Discover events defined in `lib/analytics/discover.ts`:
 *
 * - `discover_viewed` once per surface (list / map) per mount, after the
 *   result total and, for a signed-in viewer, their account are both known;
 * - `discover_filter_changed` when a URL filter changes, once that filter
 *   set's total lands (`null` if a newer change superseded it first);
 * - `discover_event_opened` on a click on any `a[data-discover-event-id]`
 *   card link. One delegated listener, so the list, map and card components
 *   only carry the data attribute.
 *
 * The total is read from the SAME `useDiscoverEventsQuery` entry every other
 * Discover subscriber uses (`useDiscoverEventArgs`), so this adds no
 * `/events/discover` request. `currentData` is argument-matched, so a total
 * is never reported against the wrong filter set.
 */

import * as React from 'react';

import type { CityCode } from '@loci/city-editions';
import { useDiscoverEventsQuery } from '@loci/api';

import { AuthContext } from '@/contexts/AuthContextRTK';
import { useDiscoverParams } from '@/hooks/useDiscoverParams';
import {
  diffDiscoverFilters,
  trackDiscoverEventOpened,
  trackDiscoverFilterChanged,
  trackDiscoverViewed,
  type DiscoverFilterChange,
  type DiscoverSurface,
} from '@/lib/analytics/discover';
import { resolveIsInternal } from '@/lib/analytics/internal';
import type { DiscoverParams } from '@/lib/discover/url-state';
import { useDiscoverViewerId } from '@/lib/discover/DiscoverViewerIdContext';
import { useDiscoverEventArgs } from '@/lib/discover/useDiscoverEventArgs';

/** Attribute every Discover event-card link carries for `discover_event_opened`. */
export const DISCOVER_EVENT_ID_ATTR = 'data-discover-event-id';

const CARD_SELECTOR = `a[${DISCOVER_EVENT_ID_ATTR}]`;

export interface DiscoverAnalyticsConnectedProps {
  cityCode: CityCode;
}

export function DiscoverAnalyticsConnected({ cityCode }: DiscoverAnalyticsConnectedProps): null {
  const { params } = useDiscoverParams();
  const queryArgs = useDiscoverEventArgs(params, cityCode);
  const { currentData, isError } = useDiscoverEventsQuery(queryArgs);
  // Read the context directly so a tree without an AuthProvider (stories,
  // isolated tests) counts as anonymous instead of throwing.
  const auth = React.useContext(AuthContext);
  const email = auth?.user?.email ?? null;
  // The server already knows whether this viewer is signed in (the SSR viewer
  // id). Until `/auth/me` returns their account, `is_internal` cannot be
  // decided: the auth bootstrap starts in a post-mount effect, so on the first
  // commit a signed-in viewer still reads as `user: null, isLoading: false`.
  const viewerId = useDiscoverViewerId();
  const accountResolved =
    auth === null ||
    (!auth.isLoading && (viewerId === undefined || auth.user !== null || auth.error !== null));

  // `undefined` = still loading; `null` = the request failed.
  const resultTotal: number | null | undefined = currentData
    ? currentData.meta.total
    : isError
      ? null
      : undefined;

  const emailRef = React.useRef(email);
  emailRef.current = email;
  const paramsRef = React.useRef(params);
  paramsRef.current = params;

  const lastViewedSurface = React.useRef<DiscoverSurface | null>(null);
  React.useEffect(() => {
    if (resultTotal === undefined || !accountResolved) return;
    if (lastViewedSurface.current === params.view) return;
    lastViewedSurface.current = params.view;
    trackDiscoverViewed({
      surface: params.view,
      city: cityCode,
      host: window.location.host,
      params,
      resultTotal,
      isInternal: resolveIsInternal(email),
    });
  }, [resultTotal, accountResolved, params, cityCode, email]);

  const previousParams = React.useRef<DiscoverParams | null>(null);
  const pendingChange = React.useRef<DiscoverFilterChange | null>(null);
  React.useEffect(() => {
    const previous = previousParams.current;
    previousParams.current = params;
    if (!previous) return;
    const change = diffDiscoverFilters(previous, params);
    if (!change) return;
    if (pendingChange.current) {
      trackDiscoverFilterChanged({
        ...pendingChange.current,
        resultTotal: null,
        isInternal: resolveIsInternal(emailRef.current),
      });
    }
    pendingChange.current = change;
  }, [params]);

  React.useEffect(() => {
    const change = pendingChange.current;
    if (!change || resultTotal === undefined) return;
    pendingChange.current = null;
    trackDiscoverFilterChanged({
      ...change,
      resultTotal,
      isInternal: resolveIsInternal(emailRef.current),
    });
  }, [resultTotal, params]);

  React.useEffect(() => {
    const handleClick = (event: MouseEvent): void => {
      if (!(event.target instanceof Element)) return;
      const link = event.target.closest(CARD_SELECTOR);
      const eventId = link?.getAttribute(DISCOVER_EVENT_ID_ATTR);
      if (!link || !eventId) return;
      const cards = Array.from(document.querySelectorAll(CARD_SELECTOR));
      trackDiscoverEventOpened({
        eventId,
        position: cards.indexOf(link),
        sort: paramsRef.current.sort,
        surface: paramsRef.current.view,
        isInternal: resolveIsInternal(emailRef.current),
      });
    };
    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, []);

  return null;
}
